import { useEffect, useRef, useState } from "react";
import VideoDisplay from "../Components/WebRTCPlayer/VideoDisplay";
import VideoControls from "../Components/WebRTCPlayer/VideoControls";
import VideoTimeline from "../Components/WebRTCPlayer/VideoTimeline";
import axios from "axios";
import MultiTimelineGraph from "../Components/WebRTCPlayer/MultiTimelineGraph";
import { v4 as uuidv4 } from "uuid";
import { formatToLocalISO, parseBackendTimestamp } from "../utils/time";
import { useSearchParams } from "react-router-dom";
import "../Components/WebRTCPlayer/WebRTCPlayer.css";
import { useCamera } from "../context/CameraProvider";
import { FaCar, FaClipboardCheck, FaFireAlt, FaHandPaper } from "react-icons/fa";
import { FaPeopleGroup } from "react-icons/fa6";
import { IoMdTime } from "react-icons/io";

import { getallUsecaseOnCamera } from "../api/api";
import Hls from "hls.js";
import StreamStatsInfo from "../Components/WebRTCPlayer/StreamStatsInfo";
import { SetPopupReducerData } from "../store/reducer/PopupReducer";
import { useDispatch } from "react-redux";

const toTimelineTimestamp = (timeStr) => {
  const time = new Date(timeStr);
  return Math.floor(time.getTime() / 1000); // UNIX timestamp in seconds
};

function flattenEvents(timeline) {
  return timeline.flatMap((entry) =>
    entry.events.map((event) => ({
      ...event,
      cameraId: entry.id,
      typeGroup: entry.type,
      color: entry.color,
    }))
  );
}

function getNearestEvents(timeline, targetTimestamp) {
  const allEvents = flattenEvents(timeline).sort((a, b) => a.timestamp - b.timestamp);

  let previous = null;
  let next = null;

  for (let i = 0; i < allEvents.length; i++) {
    const current = allEvents[i];

    if (current.timestamp < targetTimestamp) {
      previous = current;
    } else if (current.timestamp > targetTimestamp && !next) {
      next = current;
      break;
    }
  }

  // console.log("[DEBUG] previous timestamp: ", previous, "next timestamp: ", next);

  return { previous, next };
}

const transformCheckInCheckOutToTimelineEvents = (attendanceData) => {
  return attendanceData.map((entry) => ({
    timestamp: toTimelineTimestamp(entry?.in_time || entry?.out_time),
    type: "person_checkin",
    label: `${entry.employee_name} ${entry?.in_time ? "checked in" : "checked out"}`,
    data: {
      image: entry.image_dataset_path,
      in_time: entry?.in_time,
      out_time: entry?.out_time,
    },
  }));
};

const transformFireToTimelineEvents = (attendanceData) => {
  return attendanceData.map((entry) => ({
    timestamp: toTimelineTimestamp(entry?.fire_start_time),
    type: "fire",
    label: `Fire Detected`,
    data: {
      image: entry?.image_dataset_path,
    },
  }));
};

const transformNumberToTimelineEvents = (attendanceData) => {
  return attendanceData.map((entry) => ({
    timestamp: toTimelineTimestamp(entry?.updated_at),
    type: "number_plate",
    label: `Number Detected`,
    data: {
      image: entry?.image_path,
    },
  }));
};

const transformQueueWaitinglogsEvents = (attendanceData) => {
  return attendanceData.map((entry) => ({
    timestamp: toTimelineTimestamp(entry?.updated_at),
    type: "q_wait",
    label: `Queue Waiting`,
    data: {
      time_spent: entry?.time_spent,
      person_id: entry?.person_id,
    },
  }));
};

const transformCrowdDetectionEvents = (attendanceData) => {
  return attendanceData.map((entry) => ({
    timestamp: toTimelineTimestamp(entry?.updated_at),
    type: "crowd_detection",
    label: `Crowd Detection`,
    data: {
      current_people: entry?.current_people,
    },
  }));
};

const transformSeatDetectionEvents = (attendanceData) => {
  return attendanceData.map((entry) => ({
    timestamp: toTimelineTimestamp(entry?.updated_at),
    type: "seat_monitoring",
    label: `Seat Monitoring`,
    data: {
      current_people: entry?.current_people,
    },
  }));
};

const CameraPlayer = () => {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(72000000);
  const [isPlaying, setIsPlaying] = useState(false);
  const [mockTimelines, setMockTimelines] = useState([]);
  const videoRef = useRef(null);

  const [allCameras, setAllCameras] = useState([]);

  const { cameras, setCameras } = useCamera();
  const [streams, setStreams] = useState({});
  const [selectedSlotIndex, setSelectedSlotIndex] = useState(0);

  const [sessionId, setSessionId] = useState(null);
  const [userId] = useState(uuidv4());
  const [connected, setConnected] = useState(false);
  const [startTimestamp, setStartTimestamp] = useState(null);
  const [endTimestamp, setEndTimestamp] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [isUsingHLS, setIsUsingHLS] = useState(false);
  const [hlsPlaylist, setHlsPlaylist] = useState();
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const [stats, setStats] = useState({
    bitrateKbps: 0,
    packetsLost: 0,
    jitter: 0.0,
    rtt: 0.0,
    resolution: "800x600",
    fps: 0,
    codec: "video/VP8",
    framesDecoded: 0,
    framesDropped: 0,
  });

  const [searchParams] = useSearchParams();

  const id = searchParams.get("id"); // Read the current 'id' from URL

  const hlsRef = useRef(null);
  const pcRef = useRef(null);

  const fetchUsecaseLogs = async (start_date, end_date) => {
    try {
      const response = await getallUsecaseOnCamera(id, start_date, end_date);

      const logs = response?.data?.data?.logs;

      if (!logs) return;

      let mockTimelines = [];

      if (logs?.checkin_checkout_logs) {
        let checkin_checkout_logs = {
          id: "camera1",
          type: "CHECKINCHECKOUT",
          color: "#2ecc71",
          events: transformCheckInCheckOutToTimelineEvents(logs?.checkin_checkout_logs),
        };
        // console.log("[INFO] Checkin & Checkout: ", checkin_checkout_logs);
        mockTimelines.push(checkin_checkout_logs);
      }

      if (logs?.fire_detection_logs) {
        let fire_detection_logs = {
          id: "camera2",
          type: "FIRE",
          color: "#e74c3c",
          events: transformFireToTimelineEvents(logs?.fire_detection_logs),
        };
        // console.log("[INFO] Fire Detection: ", fire_detection_logs);
        mockTimelines.push(fire_detection_logs);
      }

      if (logs?.number_plate_logs) {
        let number_plate_logs = {
          id: "camera3",
          type: "NUMBERPLATE",
          color: "#ae00ffff",
          events: transformNumberToTimelineEvents(logs?.number_plate_logs),
        };
        // console.log("[INFO] Number Plate Logs: ", number_plate_logs);
        mockTimelines.push(number_plate_logs);
      }

      if (logs?.queue_waiting_logs) {
        let queue_waiting_logs = {
          id: "camera4",
          type: "QUEUEWAITING",
          color: "#3498db",
          events: transformQueueWaitinglogsEvents(logs?.queue_waiting_logs),
        };
        // console.log("[INFO] Queue Waiting Logs: ", queue_waiting_logs);
        mockTimelines.push(queue_waiting_logs);
      }

      if (logs?.crowd_detection_logs) {
        let crowd_detection_logs = {
          id: "camera5",
          type: "CROWDDETECTION",
          color: "#dbd034ff",
          events: transformCrowdDetectionEvents(logs?.crowd_detection_logs),
        };
        // console.log("[INFO] Crowd Logs: ", crowd_detection_logs);
        mockTimelines.push(crowd_detection_logs);
      }

      if (logs?.seat_monitoring_logs) {
        let seat_monitoring_logs = {
          id: "camera6",
          type: "SEATMONITORING",
          color: "#db3474ff",
          events: transformSeatDetectionEvents(logs?.seat_monitoring_logs),
        };
        // console.log("[INFO] Seat Logs: ", seat_monitoring_logs);
        mockTimelines.push(seat_monitoring_logs);
      }

      // console.log("[INFO] MockEvents: ", mockTimelines);

      setMockTimelines(mockTimelines);
    } catch (error) {
      console.error("❌ Failed to fetch filtered logs:", error.response?.data || error.message);
    }
  };

  const fetchCameras = async () => {
    const response = await axios.get(
      "https://us2.sourcesoftsolutions.com:8006/camera-details?page=1&limit=100",
      { headers: { Authorization: "Bearer test123securetoken" } }
    );
    setAllCameras(response.data?.data?.data || []);
  };

  const fetchCamerasHLS = async () => {
    try {
      const res = await axios.get(
        `https://visionemptrack.greymatterz.com/streaming/hls/${id}/playlist.m3u8`
      );
      setHlsPlaylist(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchCameras();

    const now = new Date();
    const twelveHoursAgo = new Date(now.getTime() - 12 * 60 * 60 * 1000);

    // ✅ Convert to seconds before passing
    setEndTimestamp(Math.floor(now.getTime() / 1000));
    setStartTimestamp(Math.floor(twelveHoursAgo.getTime() / 1000));
    setCurrentTime(Math.floor(now.getTime() / 1000));

    const fetchStart = Math.floor(twelveHoursAgo.getTime() / 1000);
    const fetchEnd = Math.floor(now.getTime() / 1000);

    fetchUsecaseLogs(fetchStart, fetchEnd);

    if (id) {
      setCameras(() => [id]);
      setTimeout(() => sendOffer(id, true), 100);
    }

    fetchCamerasHLS();

    const ws = new WebSocket("wss://visionemptrack.greymatterz.com/streaming/ws");

    if (ws) {
      ws.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        if (msg.event === "seeking_loading") {
          setLoading(msg?.loading);
        }
      };
    }

    return () => {
      if (ws) ws.close();
    };
  }, [id]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadStart = () => setLoading(true);
    const handleCanPlay = () => setLoading(false);
    const handleEnded = () => setLoading(false);

    video.addEventListener("loadstart", handleLoadStart);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("loadstart", handleLoadStart);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("ended", handleEnded);
    };
  }, [videoRef.current]);

  function startStatsPolling(peerConnection) {
    let lastBytes = 0;
    let lastTimestamp = 0;
    setInterval(async () => {
      const stats = await peerConnection.getStats();
      const newStats = {};
      for (const report of stats.values()) {
        if (report.type === "inbound-rtp" && report.kind === "video") {
          // console.log("📥 Inbound RTP Stats:");
          // console.log("  - Bitrate (bytesReceived):", report.bytesReceived);
          // console.log("  - Packets Lost:", report.packetsLost);
          // console.log("  - Jitter:", report.jitter);
          // if (lastTimestamp) {
          //   const bitrate =
          //     (8 * (report.bytesReceived - lastBytes)) /
          //     ((report.timestamp - lastTimestamp) / 1000);
          //   console.log("📈 Bitrate (kbps):", (bitrate / 1000).toFixed(2));
          // }
          if (lastTimestamp) {
            const bitrate =
              (8 * (report.bytesReceived - lastBytes)) /
              ((report.timestamp - lastTimestamp) / 1000);
            newStats.bitrateKbps = +(bitrate / 1000).toFixed(2);
          }
          newStats.packetsLost = report.packetsLost;
          newStats.jitter = +(report.jitter * 1000).toFixed(2); // ms
          lastBytes = report.bytesReceived;
          lastTimestamp = report.timestamp;
        }

        // if (report.type === "outbound-rtp" && report.kind === "video") {
        //   console.log("📤 Outbound RTP Stats:");
        //   console.log("  - Bitrate (bytesSent):", report.bytesSent);
        //   console.log("  - Packets Sent:", report.packetsSent);
        // }

        if (report.type === "track") {
          // console.log("🎞️ Track Stats:");
          // console.log("  - FPS:", report.framesPerSecond);
          // console.log("  - Resolution:", `${report.frameWidth}x${report.frameHeight}`);
          newStats.fps = report.framesPerSecond;
          newStats.resolution = `${report.frameWidth}x${report.frameHeight}`;
          newStats.framesDecoded = report.framesDecoded;
          newStats.framesDropped = report.framesDropped;
        }

        if (report.type === "codec") {
          // console.log("🎧 Codec Info:");
          // console.log("  - Codec:", report.mimeType);
          // console.log("  - Payload Type:", report.payloadType);

          newStats.codec = report.mimeType;
        }

        if (report.type === "candidate-pair" && report.state === "succeeded") {
          // console.log("🌐 Network Stats:");
          // console.log("  - Round Trip Time (RTT):", report.currentRoundTripTime);

          newStats.rtt = +report.currentRoundTripTime.toFixed(3); // seconds
        }
      }
      setStats(newStats);
    }, 1000);
  }

  const createPeerConnection = () => {
    const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
    pc.addTransceiver("video", { direction: "recvonly" });

    pc.ontrack = (event) => {
      console.log("[INFO]: Start Getting streams...");
      setStreams((prev) => ({ ...prev, [selectedSlotIndex]: event.streams[0] }));
    };

    pc.oniceconnectionstatechange = (e) => {
      console.log("[INFO] ICE changed");
    };

    pc.onconnectionstatechange = () => {
      console.log("[INFO] Peer state:", pc.connectionState);
    };

    startStatsPolling(pc);

    pcRef.current = pc;
    return pc;
  };

  const destroyPeerConnection = () => {
    const pc = pcRef.current;

    if (pc) {
      // Close the peer connection
      pc.close();

      // Optional: Remove tracks and stop streams
      pc.getSenders().forEach((sender) => {
        if (sender.track) sender.track.stop();
      });

      pc.getReceivers().forEach((receiver) => {
        if (receiver.track) receiver.track.stop();
      });

      pcRef.current = null;
    }

    // Clear video srcObject
    const videoEl = videoRef.current;
    if (videoEl) {
      videoEl.pause();
      videoEl.srcObject = null;
    }

    // Clear state
    setConnected(false);
    setSessionId(null);
    setIsPlaying(false);
    setStreams((prev) => ({ ...prev, [selectedSlotIndex]: null }));

    console.log("[INFO] WebRTC stream disconnected");
  };

  const sendOffer = async (
    cameraId,
    live = true,
    startTime = null,
    endTime = null,
    updateTime = true
  ) => {
    // console.log("[DEBUG] Live Attendance : ", live, startTime, endTime);

    try {
      // const cameraId = cameras[selectedSlotIndex];
      // if (!cameraId) return;

      const pc = createPeerConnection();
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const payload = {
        sdp: pc.localDescription.sdp,
        type: pc.localDescription.type,
        id: cameraId,
        user_id: userId,
        room_id: `slot${selectedSlotIndex}`,
        live,
      };

      if (!live) {
        if (!startTime || !endTime) return alert("Enter start and end times");

        payload.start_date = startTime;
        payload.end_date = endTime;
        // console.log("[DEBUG] Live Attendance : ", new Date(startTime), new Date(endTime));

        const startUnix = parseBackendTimestamp(startTime);

        if (updateTime) {
          setStartTimestamp(startUnix - 6 * 3600);
          setEndTimestamp(startUnix + 6 * 3600);
        }
        setCurrentTime(startUnix);
      } else {
        const now = Math.floor(Date.now() / 1000);
        setStartTimestamp(now - 12 * 3600);
        setEndTimestamp(now);
        setCurrentTime(now);
      }

      setLoading(true);

      const response = await fetch("https://visionemptrack.greymatterz.com/streaming/offer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const answer = await response.json();
      await pc.setRemoteDescription(answer);
      setSessionId(answer.session_id);
      setConnected(true);
      setIsPlaying(true);
    } catch (err) {
      console.error("Error in sendOffer:", err);
    } finally {
      setLoading(false);
    }
  };

  // function parseSegmentIndex(playlistText) {
  //   const lines = playlistText.split("\n");
  //   const index = [];
  //   let currentTime = 0;

  //   for (let i = 0; i < lines.length; i++) {
  //     if (lines[i].startsWith("#EXTINF")) {
  //       const duration = parseFloat(lines[i].split(":")[1]);
  //       const uri = lines[i + 1];
  //       index.push({ uri, start: currentTime });
  //       currentTime += duration;
  //     }
  //   }

  //   return index;
  // }

  function parseSegmentIndex(playlistText) {
    const lines = playlistText.split("\n").map((line) => line.trim());
    const index = [];
    let currentTime = 0;
    let discontinuityCount = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (line.startsWith("#EXT-X-DISCONTINUITY")) {
        discontinuityCount++;
        continue;
      }

      if (line.startsWith("#EXTINF")) {
        const duration = parseFloat(line.split(":")[1]);
        let uri = null;

        // Look ahead for the next .ts line
        for (let j = i + 1; j < lines.length; j++) {
          const nextLine = lines[j];
          if (nextLine.endsWith(".ts")) {
            uri = nextLine;
            break;
          }
        }

        if (uri) {
          index.push({
            uri,
            start: currentTime,
            discontinuity: discontinuityCount,
          });
          currentTime += duration;
        }
      }
    }

    return index;
  }

  function getStartTimeForSegment(index, segmentName) {
    const match = index.find((seg) => seg.uri.includes(segmentName));
    return match ? match.start : 0;
  }

  // const parseSegmentStartTime = (filename) => {
  //   const parts = filename.replace(".ts", "").split("_");
  //   const dateStr = `${parts[0]}T${parts[1].replace(/-/g, ":")}`;
  //   return new Date(dateStr).getTime() / 1000;
  // };

  const parseSegmentStartTime = (filename) => {
    const parts = filename.replace(".ts", "").split("_");
    const dateStr = `${parts[0]}T${parts[1].replace(/-/g, ":")}Z`; // Add 'Z' to indicate UTC
    return new Date(dateStr).getTime() / 1000;
  };

  const findClosestSegment = (lines, targetUnixSeconds) => {
    let closestSegment = null;
    let minDiff = Infinity;

    for (const line of lines) {
      if (!line.endsWith(".ts")) continue;

      const segmentTime = parseSegmentStartTime(line);
      const diff = Math.abs(segmentTime - targetUnixSeconds);

      if (diff < minDiff) {
        minDiff = diff;
        closestSegment = { filename: line, segmentTime };
      }
    }

    return closestSegment;
  };

  const playHlsAtTime = async (videoEl, hlsUrl, targetUnixSeconds) => {
    const response = await fetch(hlsUrl);
    const hlsPlaylist = await response.text();
    const lines = hlsPlaylist.split("\n");

    const closest = findClosestSegment(lines, targetUnixSeconds);

    // console.log("[DEBUG] closest: ", closest);

    if (!closest) {
      console.warn("No segment found near target time");
      return;
    }

    const offset = targetUnixSeconds - closest.segmentTime;

    if (offset < 0) {
      console.warn("Target time is before closest segment", closest);
      // return;
    }

    const index = parseSegmentIndex(hlsPlaylist);

    const startTime = getStartTimeForSegment(index, closest.filename);

    // if (!hlsRef.current) {
    console.info(
      "[HLS] NEW Closest segment:",
      closest.filename,
      "startTime:",
      startTime,
      "Offset:",
      offset,
      "index: ",
      index
    );
    const hls = new Hls();
    hls.loadSource(hlsUrl);
    hls.attachMedia(videoEl);

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      videoEl.currentTime = startTime + offset;
      setIsPlaying(true);
    });
    hlsRef.current = hls;

    return;
  };

  const isHlsTimestampAvailable = (targetUnix) => {
    try {
      const nowUnix = Math.floor(Date.now() / 1000);
      const twelveHoursAgo = nowUnix - 12 * 3600;

      if (targetUnix < twelveHoursAgo || targetUnix > nowUnix) {
        console.info("[INFO] Not under 12 hours");
        return false;
      }
      console.info("[INFO] Under 12 hours");

      const lines = hlsPlaylist.split("\n").filter(Boolean);

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        if (line.endsWith(".ts")) {
          const filename = line.trim();
          const segmentStart = parseSegmentFilename(filename);
          if (!segmentStart) continue;

          // console.log("[DEBUG] Segment Start: ", segmentStart, ", Unix time: ", targetUnix);

          let duration = 0;
          for (let j = i - 1; j >= 0; j--) {
            if (lines[j].startsWith("#EXTINF:")) {
              duration = parseFloat(lines[j].split(":")[1]);
              break;
            }
          }

          const segmentEnd = segmentStart + duration;
          if (targetUnix >= segmentStart - 1 && targetUnix <= segmentEnd + 1) {
            return true;
          }
        }
      }

      console.info("[INFO] Segment not exists");

      return false;
    } catch (err) {
      console.error("[ERROR] Checking segment availability:", err);
      return false;
    }
  };

  // const parseSegmentFilename = (filename) => {
  //   const match = filename.match(/(\d{4}-\d{2}-\d{2})_(\d{2})-(\d{2})-(\d{2})\.ts$/);
  //   if (!match) return null;

  //   const [_, date, hh, mm, ss] = match;

  //   // Construct ISO string in local time (IST)
  //   const localDate = new Date(`${date}T${hh}:${mm}:${ss}+05:30`);
  //   return Math.floor(localDate.getTime() / 1000);
  // };

  const parseSegmentFilename = (filename) => {
    const match = filename.match(/(\d{4}-\d{2}-\d{2})_(\d{2})-(\d{2})-(\d{2})\.ts$/);
    if (!match) return null;

    const [_, date, hh, mm, ss] = match;

    // Construct ISO string in UTC
    const utcDate = new Date(`${date}T${hh}:${mm}:${ss}Z`);
    return Math.floor(utcDate.getTime() / 1000);
  };

  const handleSeek = async (time) => {
    try {
      if (!time) return;
      if (time > endTimestamp) {
        console.warn("[WARN] Seek time exceeds endTimestamp:", time, ">", endTimestamp);
        return;
      }

      console.log("[INFO] time: ", time, ", local time: ", new Date(time * 1000));

      const hlsUrl = `https://visionemptrack.greymatterz.com/streaming/hls/${id}/playlist.m3u8`;

      const isAvailableInHLS = isHlsTimestampAvailable(time); // function defined below
      // const isAvailableInHLS = false; // function defined below

      if (isAvailableInHLS) {
        console.log("[INFO] Seeking via HLS");

        // Disconnect WebRTC if active
        destroyPeerConnection();

        const videoEl = videoRef?.current;
        if (videoEl) {
          videoEl.srcObject = null; // Clear WebRTC
          await playHlsAtTime(videoEl, hlsUrl, time);
        }

        setIsUsingHLS(true);
        setCurrentTime(time - 7);
      } else {
        console.log("[INFO] Seeking via WebRTC");
        const startDate = formatToLocalISO(time - 7);
        const endDate = formatToLocalISO(endTimestamp);

        if (!connected || !sessionId) {
          console.log("[DEBUG] Session not exists or not connected");

          sendOffer(id, false, startDate, endDate, false);
          return;
        }

        await fetch("https://visionemptrack.greymatterz.com/streaming/control", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "seek",
            user_id: userId,
            session_id: sessionId,
            start_date: startDate,
            end_date: endDate,
          }),
        });

        setIsUsingHLS(false);
        setCurrentTime(time - 7);
      }
    } catch (err) {
      console.error("Error in handleSeek:", err);
    }
  };

  function isHlsPlayerActive(hlsInstance) {
    return (
      hlsInstance instanceof Hls &&
      hlsInstance.media !== null &&
      typeof hlsInstance.media.play === "function"
    );
  }

  const handleTogglePlay = () => {
    if (isPlaying) {
      handleControl("pause");
      setIsPlaying(false);
      if (isHlsPlayerActive(hlsRef.current)) videoRef.current.pause();
    } else {
      handleControl("play");
      setIsPlaying(true);
      if (isHlsPlayerActive(hlsRef.current)) videoRef.current.play();
    }
  };

  const seek = (time) => {
    setCurrentTime(time);
  };

  const goToNextEvent = () => {
    const { next } = getNearestEvents(mockTimelines, currentTime);
    // console.log("Next Event Timestamp:", next);
    if (next && next?.timestamp) handleSeek(next?.timestamp);
  };

  const goToPreviousEvent = () => {
    const { previous } = getNearestEvents(mockTimelines, currentTime);
    // console.log("Previous Event Timestamp:", previous);
    if (previous && previous?.timestamp) handleSeek(previous?.timestamp);
  };

  const handleEventClick = (cameraId, timestamp) => {
    // console.log(`Jump to ${cameraId} @ ${timestamp}s`);
    seek(timestamp);

    handleSeek(timestamp);
  };

  const handleControl = async (action) => {
    if (!connected || !sessionId) return;
    await fetch("https://visionemptrack.greymatterz.com/streaming/control", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, user_id: userId, session_id: sessionId }),
    });
  };

  const getGridTemplateColumns = (count) => {
    if (count === 1) return "1fr";
    if (count <= 2) return "1fr 1fr";
    if (count === 3) return "1fr 1fr 1fr";
    if (count <= 4) return "1fr 1fr";
    if (count <= 6) return "1fr 1fr 1fr";
    return "repeat(auto-fit, minmax(300px, 1fr))"; // For larger camera sets
  };

  const handleToggleLive = (camera_id, time = null) => {
    if (time && !time.isLive) {
      fetchUsecaseLogs(parseBackendTimestamp(time.startDate), parseBackendTimestamp(time.endDate));

      sendOffer(camera_id, false, time.startDate, time.endDate);
    } else {
      const now = new Date();
      const twelveHoursAgo = new Date(now.getTime() - 12 * 3600 * 1000);

      fetchUsecaseLogs(
        Math.floor(twelveHoursAgo.getTime() / 1000),
        Math.floor(now.getTime() / 1000)
      );

      sendOffer(camera_id, true);
    }
  };

  const handleDownload = () => {
    dispatch(
      SetPopupReducerData({
        modalType: "SAVECLIP",
        showModal: true,
        data: {
          camera_id: id,
          startTime: "20250819T152710Z",
          endTime: "20250819T152730Z",
        },
      })
    );
  };

  return (
    <div className="video-grid">
      <div className="timeline-area">
        <div className="timeline-icons">
          {mockTimelines.map((mock) => (
            <button className="timeline-icon" style={{ backgroundColor: mock?.color }}>
              {mock?.type === "CHECKINCHECKOUT" && <FaClipboardCheck size={15} color="white" />}
              {mock?.type === "FIRE" && <FaFireAlt size={15} color="white" />}
              {mock?.type === "NUMBERPLATE" && <FaCar size={15} color="white" />}
              {mock?.type === "QUEUEWAITING" && <FaHandPaper size={15} color="white" />}
              {mock?.type === "CROWDDETECTION" && <FaPeopleGroup size={15} color="white" />}
              {mock?.type === "SEATMONITORING" && <IoMdTime size={15} color="white" />}
            </button>
          ))}
        </div>
        <VideoTimeline
          currentTime={currentTime}
          startTime={startTimestamp}
          endTime={endTimestamp}
          onSeek={seek}
          RTCSeek={handleSeek}
        >
          <MultiTimelineGraph
            timelines={mockTimelines}
            startTime={startTimestamp}
            endTime={endTimestamp}
            onEventClick={handleEventClick}
          />
        </VideoTimeline>
      </div>
      <div className="video-area">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: getGridTemplateColumns(cameras.length),
            gap: 20,
            height: "100%",
          }}
        >
          {cameras.map((cameraId, i) => {
            const camera = allCameras.find((c) => c._id === cameraId);
            return (
              <VideoDisplay
                key={i}
                camera={camera || { camera_name: "Unassigned", _id: `slot${i}` }}
                stream={streams[i]}
                videoRef={videoRef}
                isSelected={selectedSlotIndex === i}
                onSelect={() => setSelectedSlotIndex(i)}
                handleToggleLive={(time) => handleToggleLive(cameraId, time)}
                loading={loading}
                isLive={isLive}
                isUsingHLS={isUsingHLS}
              />
            );
          })}
          <StreamStatsInfo stats={stats} />
        </div>
      </div>
      <div className="controls-area">
        <VideoControls
          toggleplay={handleTogglePlay}
          isPlaying={isPlaying}
          onSeek={handleSeek}
          currentTime={currentTime}
          duration={duration}
          onNextEvent={goToNextEvent}
          onPrevEvent={goToPreviousEvent}
          handleDownload={handleDownload}
        />
      </div>
    </div>
  );
};

export default CameraPlayer;
